import React, { useCallback, useEffect, useRef, useState } from 'react';
import CheckmarkIcon from '@shared/icons/CheckmarkIcon';
import SchoolHeader from '@/components/SchoolHeader';
import SummaryBox from '@/components/SummaryBox';
import OutlineButton from '@/components/OutlineButton';
import CtaButton from '@/components/CtaButton';
import ErrorBanner from '@/components/ErrorBanner';
import PaymentMethodSection from '@/payment/PaymentMethodSection';
import { setDefaultPaymentMethod, toggleAutoPay } from '@/services/api';
import { parseHostedError } from '@/services/errors';
import { getStripe } from '@/services/stripe';
import { amountBreakdownRows, formatDate, frequencyNoun, paymentMethodExpiry, paymentMethodLabel } from '@/format';

/**
 * Web·4 — settings for an active enrollment: swap the payment method or
 * head into the turn-off warning (Web·5).
 *
 * Saving the method ALWAYS asks the server to promote the picked id — even
 * when it is the one already marked default — so the screen can never
 * report a change the server was not told about (PDL002 BE-02). `notice`
 * is the confirmation the flow shows after the status has been re-read.
 *
 * A new card that needs bank authentication leaves the page for Stripe's
 * redirect; the return lands back on this screen with the SetupIntent's
 * client secret (`resumeClientSecret`), and the promotion the save was
 * about to make happens then.
 *
 * A bank account verifying by microdeposits cannot be charged yet, so it
 * is not promoted: the server keeps the SetupIntent beside the live
 * enrollment and promotes the account when the deposits are confirmed
 * (`setup_intent.succeeded`), the same path enrolment uses.
 *
 * `status.needs_new_payment_method` is a THIRD state this screen has to
 * render, distinct from both "has a method" and "never set one up": the
 * school stopped accepting the rail the saved method sits on, so the API
 * reports no default while one is still on file. The enrollment stands --
 * consent has not changed, and the school may turn the rail back on -- but
 * nothing can be collected, so the screen must not show the green banner,
 * must not print a next-charge date, and must say WHY. Showing a bare dash
 * for the method was the bug: a family whose card was fine saw a screen
 * that looked like Auto-Pay had never been configured.
 */
const ManageScreen = ({
	school,
	status,
	assignment,
	notice,
	onTurnOff,
	onUpdated,
	onNoticeDismiss,
	resumeClientSecret,
	onResumeConsumed,
	onPendingBank,
}) => {
	const [updating, setUpdating] = useState(false);
	const [busy, setBusy] = useState(false);
	const [error, setError] = useState(null);
	const [selection, setSelection] = useState(null);
	const confirmRef = useRef(null);
	const handleBridge = useCallback((fn) => {
		confirmRef.current = fn;
	}, []);

	const studentId = status.student_id;
	const nextChargeAt = assignment.next_charge_at ?? assignment.first_charge_at;
	const defaultPm = status.default_payment_method;

	// The school stopped accepting the rail this family's saved method sits
	// on, so the API reports no default even though one is on file. Auto-Pay
	// stays enrolled and collects nothing until they add a method it can
	// charge -- which is the whole failure this screen has to stop hiding.
	const needsNewMethod = Boolean(status.needs_new_payment_method);

	const awaitingMicrodeposits = (setupIntent) =>
		setupIntent?.status === 'requires_action' && setupIntent.next_action?.type === 'verify_with_microdeposits';

	// The mandate was accepted at enrolment and is kept; only the pending
	// intent rides alongside the live enrollment until it verifies.
	const registerPendingBank = async (setupIntent) => {
		await toggleAutoPay({ student_id: studentId, enabled: true, pending_setup_intent_id: setupIntent.id });
		setUpdating(false);
		onPendingBank(setupIntent.next_action?.verify_with_microdeposits ?? null);
	};

	// Post-redirect return: the card was authenticated with the bank but the
	// page never reached the promotion. Finish it now, or reopen the panel
	// with the reason if the intent did not succeed.
	useEffect(() => {
		if (!resumeClientSecret || !status.stripe_account_id) return;

		const resume = async () => {
			setBusy(true);
			try {
				const stripe = await getStripe(import.meta.env.VITE_STRIPE_PUBLISHABLE_KEY, status.stripe_account_id);
				const { setupIntent } = await stripe.retrieveSetupIntent(resumeClientSecret);
				if (setupIntent?.status === 'succeeded' && setupIntent.payment_method) {
					await setDefaultPaymentMethod(setupIntent.payment_method, studentId);
					onUpdated();
					return;
				}
				if (awaitingMicrodeposits(setupIntent)) {
					await registerPendingBank(setupIntent);
					return;
				}
				setUpdating(true);
				setError('Your new payment method wasn’t fully set up — please try again.');
			} catch (err) {
				setUpdating(true);
				setError(parseHostedError(err).message);
			} finally {
				setBusy(false);
				onResumeConsumed();
			}
		};

		resume();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [resumeClientSecret, status.stripe_account_id]);

	const openUpdate = () => {
		setSelection(null);
		setError(null);
		onNoticeDismiss?.();
		setUpdating(true);
	};

	const cancelUpdate = () => {
		setSelection(null);
		setError(null);
		setUpdating(false);
	};

	const handleSaveMethod = async () => {
		setError(null);
		if (!selection || (selection.type === 'new' && !confirmRef.current)) {
			setError('Pick a saved card or add a new one first.');
			return;
		}
		setBusy(true);
		try {
			let pmId = selection.pmId;
			if (selection.type === 'new') {
				const returnUrl = `${window.location.origin}/auto-pay${window.location.search}`;
				const { setupIntent, error: stripeError } = await confirmRef.current(returnUrl);
				if (stripeError) {
					setError(stripeError.message);
					return;
				}
				if (awaitingMicrodeposits(setupIntent)) {
					await registerPendingBank(setupIntent);
					return;
				}
				pmId = setupIntent.payment_method;
			}
			await setDefaultPaymentMethod(pmId, studentId);
			setUpdating(false);
			onUpdated();
		} catch (err) {
			setError(parseHostedError(err).message);
		} finally {
			setBusy(false);
		}
	};

	return (
		<div className="flex flex-col gap-[18px]">
			<SchoolHeader name={school?.name} logo={school?.logo} />

			<h1 className="text-[24px] font-extrabold text-pay-ink">Auto-Pay settings</h1>

			{needsNewMethod ? (
				/*
				 * NOT the green "Auto-Pay is on" banner, and not an error
				 * either. Auto-Pay IS still enrolled -- the enrollment is the
				 * family's consent and nothing has withdrawn it -- but it has
				 * nothing chargeable, so the next charge date below would be a
				 * promise that cannot be kept. Naming the cause matters: a
				 * parent told only to update their method checks the card,
				 * finds it perfectly valid, and ignores the message.
				 */
				<div className="flex w-full items-start gap-[12px] rounded-[14px] bg-pay-warn-bg px-[14px] py-[12px]">
					<div className="flex flex-col gap-[2px]">
						<p className="text-[15px] font-bold text-pay-ink">Auto-Pay needs a new payment method</p>
						<p className="text-[13px] text-pay-muted">
							{school?.name ?? 'Your school'} no longer accepts the payment method you had saved. Nothing will be
							charged until you add one it accepts.
						</p>
					</div>
				</div>
			) : (
				<div className="flex w-full items-center gap-[12px] rounded-[14px] bg-pay-green-bg px-[14px] py-[12px]">
					<div className="flex h-[24px] w-[24px] shrink-0 items-center justify-center rounded-full bg-pay-green text-white">
						<CheckmarkIcon width={13} height={13} />
					</div>
					<div className="flex flex-col gap-[2px]">
						<p className="text-[15px] font-bold text-pay-green-dark">Auto-Pay is on</p>
						{nextChargeAt ? <p className="text-[13px] text-pay-muted">Next charge {formatDate(nextChargeAt)}</p> : null}
					</div>
				</div>
			)}

			{notice ? (
				<p
					role="status"
					className="rounded-[14px] bg-pay-green-bg px-[14px] py-[10px] text-[13px] font-semibold text-pay-green-dark"
				>
					{notice}
				</p>
			) : null}

			{updating ? (
				<>
					<PaymentMethodSection
						studentId={studentId}
						stripeAccountId={status.stripe_account_id}
						defaultPm={defaultPm}
						onSelectionChange={setSelection}
						onBridge={handleBridge}
						forceExpanded
					/>
					<ErrorBanner>{error}</ErrorBanner>
					<CtaButton onClick={handleSaveMethod} disabled={busy}>
						{busy ? 'Saving…' : 'Save payment method'}
					</CtaButton>
					<button
						type="button"
						onClick={cancelUpdate}
						disabled={busy}
						className="w-full text-center text-[13px] font-semibold text-pay-muted"
					>
						Cancel
					</button>
				</>
			) : (
				<>
					<SummaryBox
						white
						rows={[
							{
								label: 'Payment method',
								value: (
									<span className="flex items-center gap-[10px]">
										{/*
										 * A dash here is what made this screen unreadable: the
										 * family had a method, it stopped being chargeable, and
										 * the row looked like nothing had ever been set up.
										 */}
										{paymentMethodLabel(defaultPm) ?? (needsNewMethod ? 'Needs replacing' : '—')}
										{paymentMethodExpiry(defaultPm) ? (
											<span className="text-[13px] font-normal text-pay-muted">{paymentMethodExpiry(defaultPm)}</span>
										) : null}
										<button type="button" onClick={openUpdate} className="font-semibold text-pd-blue-new">
											Update
										</button>
									</span>
								),
							},
							...amountBreakdownRows(assignment),
							{ label: 'Frequency', value: frequencyNoun(assignment.frequency) },
							{
								label: 'Next charge',
								// Not a date while there is nothing to charge with: the
								// cycle still comes round, but no money moves on it.
								value: needsNewMethod ? 'Paused' : nextChargeAt ? formatDate(nextChargeAt) : '—',
							},
						]}
					/>

					<ErrorBanner>{error}</ErrorBanner>

					{needsNewMethod ? (
						<CtaButton onClick={openUpdate}>Add a payment method</CtaButton>
					) : (
						<OutlineButton onClick={openUpdate}>Update payment method</OutlineButton>
					)}

					{status.autopay_required_by_school ? (
						<p className="w-full text-center text-[13px] text-pay-muted">
							{school?.name ?? 'Your school'} requires Auto-Pay for tuition plans, so it can’t be turned off here.
						</p>
					) : (
						<OutlineButton variant="red" onClick={onTurnOff}>
							Turn off Auto-Pay
						</OutlineButton>
					)}
				</>
			)}
		</div>
	);
};

export default ManageScreen;
